'use server'

import { apiRequest, ApiResponse } from '../request'
import { getClient } from '../client'
import { getStoredToken } from '@/lib/api/session-refresh'

export type MedicalReportType =
    | 'CONSULTATION'
    | 'EXAM_REPORT'
    | 'PROCEDURE_REPORT'
    | 'DISCHARGE_SUMMARY'
    | 'EVOLUTION'
    | 'CERTIFICATE'
    | 'REFERRAL'
    | 'OTHER'

export interface MedicalReportAttachment {
    fileName: string
    filePath: string
    userName: string
    description: string
    details?: string | null
    createdAt?: string
}

/** @deprecated Só o vínculo que o laudo carrega; o modelo agora é `Template`. */
export interface MedicalReportTemplate {
    id: number
    name: string
    type?: MedicalReportType
    content: string
    description?: string
    companyId: number
    createdAt: string
    updatedAt: string
}

export interface MedicalReport {
    id: number
    title: string
    type: MedicalReportType
    content: string
    summary?: string
    conclusion?: string
    reportDate: string
    signedAt?: string
    signedBy?: string
    digitalSignature?: string
    signedPdfUrl?: string
    signatureData?: any
    signatureValid?: boolean
    clientId: number
    client?: {
        id: number
        name: string
        email?: string
        whatsAppNumber?: string
    }
    professionalId: number
    professional?: {
        id: number
        name: string
        registrationNumber?: string
        councilName?: string
    }
    partnerId?: number
    partner?: {
        id: number
        name: string
        tradingName?: string
    }
    companyId: number
    company?: {
        id: number
        name: string
        tradingName?: string
        phone?: string
        email?: string
        street?: string
        number?: string
        neighborhood?: string
        city?: string
        state?: string
        zipCode?: string
    }
    templateId?: number
    template?: MedicalReportTemplate
    attachments?: MedicalReportAttachment[]
    metadata?: any
    createdAt: string
    updatedAt: string
}

export interface CreateMedicalReportData {
    title: string
    type?: MedicalReportType
    content: string
    summary?: string
    conclusion?: string
    clientId: number
    professionalId: number
    partnerId?: number
    companyId: number
    templateId?: number
    attachments?: any
    metadata?: any
}

export interface UpdateMedicalReportData extends Partial<CreateMedicalReportData> {}

export interface CreateTemplateData {
    name: string
    type?: MedicalReportType
    content: string
    description?: string
    companyId: number
}

export interface UpdateTemplateData extends Partial<CreateTemplateData> {}

export async function getMedicalReports(
    companyId?: number
): Promise<ApiResponse<MedicalReport[]>> {
    const params = companyId ? { companyId } : {}
    return apiRequest<MedicalReport[]>('medical-reports', { params })
}

export async function getMedicalReportsByCompany(
    companyId: number,
    params?: { partnerId?: number; page?: number; limit?: number; search?: string },
): Promise<ApiResponse<MedicalReport[]>> {
    return apiRequest<MedicalReport[]>(`medical-reports/company/${companyId}`, {
        params: params || {},
    })
}

export async function getMedicalReportsByClient(
    clientId: number
): Promise<ApiResponse<MedicalReport[]>> {
    return apiRequest<MedicalReport[]>(`medical-reports/client/${clientId}`, {})
}

export async function getMedicalReportsByProfessional(
    professionalId: number
): Promise<ApiResponse<MedicalReport[]>> {
    return apiRequest<MedicalReport[]>(
        `medical-reports/professional/${professionalId}`,
        {}
    )
}

export async function getMedicalReportsByType(
    companyId: number,
    type: MedicalReportType
): Promise<ApiResponse<MedicalReport[]>> {
    return apiRequest<MedicalReport[]>(`medical-reports/type/${type}`, {
        params: { companyId },
    })
}

export async function getMedicalReport(
    id: number
): Promise<ApiResponse<MedicalReport>> {
    return apiRequest<MedicalReport>(`medical-reports/${id}`, {})
}

export async function getMedicalReportPrintData(
    id: number
): Promise<ApiResponse<MedicalReport>> {
    return apiRequest<MedicalReport>(`medical-reports/${id}/print`, {})
}

export async function createMedicalReport(
    data: CreateMedicalReportData
): Promise<ApiResponse<MedicalReport>> {
    return apiRequest<MedicalReport>('medical-reports', { data }, 'post')
}

export async function updateMedicalReport(
    id: number,
    data: UpdateMedicalReportData
): Promise<ApiResponse<MedicalReport>> {
    return apiRequest<MedicalReport>(`medical-reports/${id}`, { data }, 'patch')
}

export async function deleteMedicalReport(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`medical-reports/${id}`, {}, 'delete')
}

export async function uploadMedicalReportAttachment(
    id: number,
    file: File,
    userName: string,
    description: string,
    details?: string
): Promise<ApiResponse<MedicalReport>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('userName', userName)
    formData.append('description', description)
    if (details) {
        formData.append('details', details)
    }

    const client = getClient(true)
    // A chave 'token' nunca existiu neste projeto — estas chamadas iam sem
    // credencial. O access token vive em memória.
    const token = getStoredToken()

    const response = await client.post(
        `medical-reports/${id}/attachments`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${token}`,
            },
        }
    )

    return {
        success: true,
        result: response.data,
    }
}

export async function deleteMedicalReportAttachment(
    id: number,
    attachmentIndex: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `medical-reports/${id}/attachments/${attachmentIndex}`,
        {},
        'delete'
    )
}

export async function signMedicalReport(
    id: number,
    signedBy: string,
    digitalSignature?: string
): Promise<ApiResponse<MedicalReport>> {
    return apiRequest<MedicalReport>(
        `medical-reports/${id}/sign`,
        { data: { signedBy, digitalSignature } },
        'post'
    )
}

// Template functions
// O CRUD de template saiu daqui: `MedicalReportTemplate` virou `Template`, que
// serve também a pedido de exame e atestado e é do PROFISSIONAL, não da empresa.
// Ver `lib/api/templates/template-api.ts`.

export const MEDICAL_REPORT_TYPE_LABELS: Record<MedicalReportType, string> = {
    CONSULTATION: 'Consulta',
    EXAM_REPORT: 'Relatório de Exame',
    PROCEDURE_REPORT: 'Relatório de Procedimento',
    DISCHARGE_SUMMARY: 'Sumário de Alta',
    EVOLUTION: 'Evolução',
    CERTIFICATE: 'Certificado',
    REFERRAL: 'Encaminhamento',
    OTHER: 'Outro',
}

export const MEDICAL_REPORT_TYPE_COLORS: Record<MedicalReportType, string> = {
    CONSULTATION: 'badge-secondary',
    EXAM_REPORT: 'badge-info',
    PROCEDURE_REPORT: 'badge-warning',
    DISCHARGE_SUMMARY: 'badge-success',
    EVOLUTION: 'badge-info',
    CERTIFICATE: 'badge-accent',
    REFERRAL: 'badge-secondary',
    OTHER: 'badge-ghost',
}
